import type { State } from '../State';
import * as Duo from '.';
import * as Pet from '../pet';
import features from '../ruleset/features';
import { weightedRandom } from '../utils/random';
import { PlayerID } from '../friends/friends.ruleset';

export const hasMetUnlockCriteria = (state: State) =>
  Pet.getters.getCurrentEvolution(state)?.stage === 'adult';

export function isUnlocked(state: State): boolean {
  if (!features(state).duo) {
    return false;
  }

  return hasMetUnlockCriteria(state);
}
export function hasPendingSentInvitations(state: State, now: number): boolean {
  const { visitSession } = state.visit;
  if (!visitSession) {
    return false;
  }

  return getNonExpiredSentInvitations(state, now).some(
    ({ playerId, status }) =>
      visitSession.friendId === playerId && status === 'pending',
  );
}

export function hasPendingReceivedInvitations(
  state: State,
  now: number,
): boolean {
  const { visitSession } = state.visit;
  if (!visitSession) {
    return false;
  }

  return getNonExpiredReceivedInvitations(state, now).some(
    ({ playerId, status }) =>
      visitSession.friendId === playerId && status === 'pending',
  );
}

export function getNonExpiredSentInvitations(state: State, now: number) {
  const { sent } = state.duo.invitations;

  return sent.filter(
    ({ timestamp }) => !Duo.utils.isExpiredInvitation(timestamp, now),
  );
}

export function getNonExpiredReceivedInvitations(state: State, now: number) {
  const {
    duo: {
      invitations: { received },
    },
  } = state;

  return received.filter(
    ({ timestamp }) => !Duo.utils.isExpiredInvitation(timestamp, now),
  );
}

export function getPendingSentDuoInvitationToFriend(
  state: State,
  now: number,
  friendId: PlayerID,
) {
  const invitations = getNonExpiredSentInvitations(state, now);
  return invitations.find(
    (invitation) =>
      invitation.playerId === friendId && invitation.status === 'pending',
  );
}

export function getPendingReceivedDuoInvitationFromFriend(
  state: State,
  now: number,
  friendId: PlayerID,
) {
  const invitations = getNonExpiredReceivedInvitations(state, now);
  return invitations.find(
    (invitation) =>
      invitation.playerId === friendId && invitation.status === 'pending',
  );
}

export function getNewSpecies(
  weights: Record<string, number>[],
  random: () => number,
): string {
  // Sum up all weights.
  const combinedWeights = weights.reduce((acc, cur) => {
    Object.keys(cur).forEach((key) => {
      acc[key] = (acc[key] || 0) + cur[key];
    });
    return acc;
  }, {});

  // Randomly pick a species.
  const roll = weightedRandom(Object.values(combinedWeights), random);
  return Object.keys(combinedWeights)[roll];
}

export function getLastUnseenInvitation(state: State, now: number) {
  const isAdult = Pet.getters.getCurrentEvolution(state)?.stage === 'adult';

  // Do not show pending invitations if the player is not an adult.
  if (!isAdult) {
    return null;
  }

  const invitations = Duo.getters.getNonExpiredReceivedInvitations(state, now);
  const latestInvitation = invitations[invitations.length - 1];

  if (!latestInvitation || latestInvitation.timestamp < state.duo.lastViewed) {
    return null;
  }

  return latestInvitation;
}
